import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';

import { abeSalonVatRead, ibeSalonVatUpdate } from 'actions/salon/beSalonVat.action';
import Permitted from 'components/core/Permitted.component';
import ToggleSwitch from 'components/form/ToggleSwitch.component';
import ToggleSwitchPanel from 'components/form/ToggleSwitchPanel.component';
import Icon from 'components/generic/Icon.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import PERM from 'constants/core/perm.const';
import useAsyncFnFix from 'hooks/useAsyncFnFix.hook';
import { useTranslation } from 'providers/GILT.provider';
import mapActionsToDispatchToProps from 'utils/core/mapActionsToDispatchToProps.util';
import { locationReload } from 'utils/dom/location.util';
import logger from 'utils/logger.util';

import CN from './ToggleVat.module.scss';

const L = logger('ToggleVat');

const ToggleVat = ({ abeSalonVatRead, ibeSalonVatUpdate }) => {
  const { t } = useTranslation();

  const [vatReadResult, handleVatRead] = useAsyncFnFix(async () => {
    const result = await abeSalonVatRead();
    if (!result.is.ok) L.warn('handleVatRead()', 'result:', result);
    return result;
  }, [abeSalonVatRead]);

  const [vatUpdateResult, handleVatUpdate] = useAsyncFnFix(
    async (ev) => {
      const result = await ibeSalonVatUpdate({ data: { isVatRegistered: ev?.value } });
      if (!result?.is?.ok) L.warn('handleVatUpdate()', 'result:', result);
      return result;
    },
    [ibeSalonVatUpdate],
  );

  const handleIncludeVat = useCallback(() => {
    handleVatUpdate({ value: true }).then(() => {
      locationReload({ tag: 'ToggleVat__include-vat' });
    });
  }, [handleVatUpdate]);

  const handleExcludeVat = useCallback(() => {
    handleVatUpdate({ value: false }).then(() => {
      locationReload({ tag: 'ToggleVat__exclude-vat' });
    });
  }, [handleVatUpdate]);

  const isSpinnerRendered = vatUpdateResult.loading || vatReadResult.loading;
  const isAlreadyInitialized = vatReadResult?.value || vatReadResult.loading;
  const isVatEnabled = vatUpdateResult.value?.json?.isVatRegistered ?? vatReadResult.value?.json?.isVatRegistered;

  const isVatDisabled = !isVatEnabled;

  useEffect(() => {
    if (isAlreadyInitialized) return;
    // noinspection JSIgnoredPromiseFromCall
    handleVatRead();
  }, [handleVatRead, isAlreadyInitialized]);

  if (vatUpdateResult.error || vatReadResult.error) L.warn('()', { vatUpdateResult, vatReadResult });

  L.debug('()', { isSpinnerRendered, isVatEnabled }, { vatReadResult, vatUpdateResult });

  return (
    <Permitted value={PERM.canViewKpiReportsAllStaff}>
      <div data-bem="ToggleVat" className={CN.component}>
        {isSpinnerRendered && <OverlaySpinner />}

        <ToggleSwitchPanel>
          <ToggleSwitch neutral selected={isVatDisabled} onClick={handleExcludeVat} name={t('staffKpisReports.kpiReports.rangeFilter.includeVat')} size="small" tag="ToggleVat__include-vat">
            <ResponsiveBox.Desktop>
              <Icon variant="radioCheckNeutral" selected={isVatDisabled} width="lg" />
            </ResponsiveBox.Desktop>
          </ToggleSwitch>

          <ToggleSwitch neutral selected={isVatEnabled} onClick={handleIncludeVat} name={t('staffKpisReports.kpiReports.rangeFilter.excludeVat')} size="small" tag="ToggleVat__exclude-vat">
            <ResponsiveBox.Desktop>
              <Icon variant="radioCheckNeutral" selected={isVatEnabled} width="lg" />
            </ResponsiveBox.Desktop>
          </ToggleSwitch>
        </ToggleSwitchPanel>
      </div>
    </Permitted>
  );
};

const ToggleVatHoc = connect(
  null,
  mapActionsToDispatchToProps({
    abeSalonVatRead,
    ibeSalonVatUpdate,
  }),
)(ToggleVat);

export default ToggleVatHoc;
